import React, { useState, useEffect } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Edit, Trash2, FileText, Plus } from "lucide-react";
import "../../styles/ActionsTable.css";
import apiClient from "../../Api/apiConfig";

const columnHelper = createColumnHelper();

export default function ActionsTable() {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedAction, setSelectedAction] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [popupType, setPopupType] = useState(""); // "edit", "delete", "add"
  const [newAction, setNewAction] = useState({
    title: "",
    description: "",
    date: "",
    type: "image",
    media_url: "",
  });

  // Charger les actions depuis l'API
  useEffect(() => {
    const fetchActions = async () => {
      try {
        // ✅ NE PAS remettre /api ici, apiClient a déjà la base /api
        const response = await apiClient.get("/actions");
        console.log("[Admin Actions] Réponse API /actions :", response.data);
        setData(response.data);
        setIsLoading(false);
      } catch (err) {
        console.error("Erreur lors du chargement des actions :", err);
        setError("Erreur lors du chargement des données");
        setIsLoading(false);
      }
    };

    fetchActions();
  }, []);

  const openPopup = (type, action = null) => {
    setPopupType(type);
    setSelectedAction(action);
    setShowPopup(true);
    if (type === "add") {
      setNewAction({
        title: "",
        description: "",
        date: "",
        type: "image",
        media_url: "",
      });
    }
  };

  const closePopup = () => {
    setShowPopup(false);
    setSelectedAction(null);
    setPopupType("");
  };

  const saveEditedAction = async () => {
    if (!selectedAction) return;
    try {
      await apiClient.put(`/actions/${selectedAction.id}`, selectedAction);
      setData((prevData) =>
        prevData.map((action) =>
          action.id === selectedAction.id ? selectedAction : action
        )
      );
      closePopup();
    } catch (err) {
      console.error("Erreur lors de la mise à jour de l'action :", err);
    }
  };

  const confirmDeleteAction = async () => {
    if (!selectedAction) return;
    try {
      await apiClient.delete(`/actions/${selectedAction.id}`);
      setData((prevData) =>
        prevData.filter((action) => action.id !== selectedAction.id)
      );
      closePopup();
    } catch (err) {
      console.error("Erreur lors de la suppression de l'action :", err);
    }
  };

  const saveNewAction = async () => {
    try {
      const response = await apiClient.post("/actions", newAction);
      setData((prevData) => [...prevData, response.data]);
      closePopup();
    } catch (err) {
      console.error("Erreur lors de l'ajout d'une action :", err);
    }
  };

  const columns = [
    columnHelper.accessor("title", {
      header: "Titre",
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor("date", {
      header: "Date",
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor("description", {
      header: "Description",
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor("type", {
      header: "Type",
      cell: (info) => (
        <span className="flex items-center">
          <FileText className="h-5 w-5 text-blue-600 mr-2" />
          {info.getValue() === "image" ? "Image" : "Vidéo"}
        </span>
      ),
    }),
    columnHelper.accessor("id", {
      header: "Actions",
      cell: (info) => (
        <div className="flex space-x-2">
          <button
            onClick={() => openPopup("edit", info.row.original)}
            className="p-1 text-blue-600 hover:text-blue-800"
          >
            <Edit className="h-5 w-5" />
          </button>
          <button
            onClick={() => openPopup("delete", info.row.original)}
            className="p-1 text-red-600 hover:text-red-800"
          >
            <Trash2 className="h-5 w-5" />
          </button>
        </div>
      ),
    }),
  ];

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isLoading) {
    return <p>Chargement...</p>;
  }

  if (error) {
    return <p className="text-red-500">{error}</p>;
  }

  return (
    <div className="mt-8 flex flex-col bg-white shadow rounded-lg">
      <div className="p-4 border-b border-gray-200 flex justify-between items-center">
        <h2 className="text-lg font-medium text-gray-900">
          Gestion des actions
        </h2>
        <button
          onClick={() => openPopup("add")}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
        >
          <Plus className="h-5 w-5 mr-2" />
          Ajouter une action
        </button>
      </div>
      <div className="-mx-4 -my-2 overflow-x-auto">
        <div className="inline-block min-w-full py-2 align-middle">
          <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Popup */}
      {showPopup && (
        <div className="popup-overlay">
          <div className="popup-content">
            {popupType === "edit" || popupType === "add" ? (
              <>
                <h3>
                  {popupType === "edit"
                    ? "Modifier l'action"
                    : "Ajouter une action"}
                </h3>
                <form>
                  <label>
                    Titre
                    <input
                      type="text"
                      value={
                        popupType === "edit"
                          ? selectedAction?.title || ""
                          : newAction.title
                      }
                      onChange={(e) =>
                        popupType === "edit"
                          ? setSelectedAction({
                              ...selectedAction,
                              title: e.target.value,
                            })
                          : setNewAction({
                              ...newAction,
                              title: e.target.value,
                            })
                      }
                    />
                  </label>
                  <label>
                    Description
                    <textarea
                      value={
                        popupType === "edit"
                          ? selectedAction?.description || ""
                          : newAction.description
                      }
                      onChange={(e) =>
                        popupType === "edit"
                          ? setSelectedAction({
                              ...selectedAction,
                              description: e.target.value,
                            })
                          : setNewAction({
                              ...newAction,
                              description: e.target.value,
                            })
                      }
                    />
                  </label>
                  <label>
                    Date
                    <input
                      type="date"
                      value={
                        popupType === "edit"
                          ? selectedAction?.date || ""
                          : newAction.date
                      }
                      onChange={(e) =>
                        popupType === "edit"
                          ? setSelectedAction({
                              ...selectedAction,
                              date: e.target.value,
                            })
                          : setNewAction({
                              ...newAction,
                              date: e.target.value,
                            })
                      }
                    />
                  </label>
                  <label>
                    Type
                    <select
                      value={
                        popupType === "edit"
                          ? selectedAction?.type || "image"
                          : newAction.type
                      }
                      onChange={(e) =>
                        popupType === "edit"
                          ? setSelectedAction({
                              ...selectedAction,
                              type: e.target.value,
                            })
                          : setNewAction({
                              ...newAction,
                              type: e.target.value,
                            })
                      }
                    >
                      <option value="image">Image</option>
                      <option value="video">Vidéo</option>
                    </select>
                  </label>
                  <label>
                    URL Média
                    <input
                      type="text"
                      value={
                        popupType === "edit"
                          ? selectedAction?.media_url || ""
                          : newAction.media_url
                      }
                      onChange={(e) =>
                        popupType === "edit"
                          ? setSelectedAction({
                              ...selectedAction,
                              media_url: e.target.value,
                            })
                          : setNewAction({
                              ...newAction,
                              media_url: e.target.value,
                            })
                      }
                    />
                  </label>
                  <div className="form-actions">
                    <button
                      type="button"
                      onClick={
                        popupType === "edit" ? saveEditedAction : saveNewAction
                      }
                      className="text-blue-600"
                    >
                      {popupType === "edit" ? "Enregistrer" : "Ajouter"}
                    </button>
                    <button
                      type="button"
                      onClick={closePopup}
                      className="text-gray-600"
                    >
                      Annuler
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <>
                <h3>Supprimer l'action</h3>
                <p>Êtes-vous sûr de vouloir supprimer cette action ?</p>
                <div className="form-actions">
                  <button
                    type="button"
                    onClick={confirmDeleteAction}
                    className="text-red-600"
                  >
                    Supprimer
                  </button>
                  <button
                    type="button"
                    onClick={closePopup}
                    className="text-gray-600"
                  >
                    Annuler
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
